import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  EyeOff,
  FileText,
  Image as ImageIcon,
  Loader2,
  Palette,
  Search,
  Send,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import type {
  EnterpriseListItem,
  FormQuestion,
  PartnerDetail,
  PartnerForm,
  PartnerFormUpdateInput,
  PartnerUpdateInput,
  ProptreeEnumValue,
} from '@proptr/shared';
import { ApiError } from '@proptr/api-client';
import { AppShell } from '../components/layout/AppShell.js';
import { PartnerButtonPreview } from '../components/PartnerButtonPreview.js';
import { QuestionsEditor } from '../components/QuestionsEditor.js';
import { SectionCard } from '../components/SectionCard.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { Select, type SelectOption } from '../components/ui/select.js';
import { Switch } from '../components/ui/switch.js';
import { uploadPartnerLogo } from '../lib/uploadImage.js';
import { client } from '../main.js';
import { ColorField } from './PartnersPage.js';

const PUBLIC_URL =
  (import.meta.env.VITE_PUBLIC_URL as string | undefined) ?? 'http://localhost:4040';

// Partner forms always run the questions first and never verify the
// visitor — the contact step is plain inputs, so those are the only
// contact choices.
const LAYOUT_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Multi page', value: 'multi_page' },
  { id: 2, name: 'Single page', value: 'single_page' },
];
const LANGUAGE_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'English', value: 'en' },
  { id: 2, name: 'Swedish', value: 'sv' },
  { id: 3, name: 'Arabic', value: 'ar' },
  { id: 4, name: 'German', value: 'de' },
];
const CONTACT_FIELDS = [
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
] as const;

interface PartnerState {
  name: string;
  subtitle: string;
  ctaLabel: string;
  color: string;
  logo: string | null;
  leadEndpoint: string;
  active: boolean;
  excluded: Array<{ _id: string; name: string }>;
}
interface FormState {
  questions: FormQuestion[];
  layout: ProptreeEnumValue | null;
  language: ProptreeEnumValue | null;
  contactFields: { email: boolean; phone: boolean };
}

const toPartnerState = (p: PartnerDetail): PartnerState => ({
  name: p.name,
  subtitle: p.subtitle ?? '',
  ctaLabel: p.ctaLabel,
  color: p.color,
  logo: p.logo,
  leadEndpoint: p.leadEndpoint ?? '',
  active: p.active,
  excluded: p.excluded,
});
const toFormState = (f: PartnerForm): FormState => ({
  questions: f.questions,
  layout: f.layout,
  language: f.language,
  contactFields: f.contactFields,
});
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function PartnerDetailPage() {
  const { partnerId } = useParams<{ partnerId: string }>();
  const [data, setData] = React.useState<{ partner: PartnerDetail; form: PartnerForm } | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!partnerId) return;
    let alive = true;
    Promise.all([
      client.admin.partners.get(partnerId),
      client.admin.partners.getForm(partnerId),
    ])
      .then(([partner, form]) => alive && setData({ partner, form }))
      .catch((err: unknown) =>
        alive &&
        setError(
          err instanceof ApiError && err.status === 404
            ? 'No partner with that id.'
            : (err as Error).message,
        ),
      );
    return () => {
      alive = false;
    };
  }, [partnerId]);

  return (
    <AppShell>
      <div className="space-y-6 pb-10">
        <div className="space-y-3">
          <Link
            to="/partners"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            All partners
          </Link>
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
            {data?.partner.name ?? 'Partner'}
          </h1>
          {data && (
            <div className="font-mono text-[11px] text-muted-foreground">
              Key <span className="text-foreground">{data.partner.key}</span> · stored in
              each proptree’s partner toggles
            </div>
          )}
        </div>
        {error && (
          <Card className="border-destructive/40 bg-destructive/10 p-4 font-mono text-[11px] text-destructive">
            {error}
          </Card>
        )}
        {!data && !error && (
          <Card className="p-6 font-mono text-[11px] text-muted-foreground">Loading…</Card>
        )}
        {data && partnerId && (
          <Editor partnerId={partnerId} initialPartner={data.partner} initialForm={data.form} />
        )}
      </div>
    </AppShell>
  );
}

function Editor({
  partnerId,
  initialPartner,
  initialForm,
}: {
  partnerId: string;
  initialPartner: PartnerDetail;
  initialForm: PartnerForm;
}) {
  const [partner, setPartner] = React.useState(() => toPartnerState(initialPartner));
  const [savedPartner, setSavedPartner] = React.useState(() => toPartnerState(initialPartner));
  const [form, setForm] = React.useState(() => toFormState(initialForm));
  const [savedForm, setSavedForm] = React.useState(() => toFormState(initialForm));
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const setP = <K extends keyof PartnerState>(k: K, v: PartnerState[K]) =>
    setPartner((s) => ({ ...s, [k]: v }));
  const setF = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((s) => ({ ...s, [k]: v }));

  const partnerDirty = !same(partner, savedPartner);
  const formDirty = !same(form, savedForm);
  const dirty = partnerDirty || formDirty;
  const colorValid = /^#[0-9a-f]{6}$/i.test(partner.color);
  const endpointValid = !partner.leadEndpoint.trim() || /^https?:\/\/\S+$/i.test(partner.leadEndpoint.trim());
  const canSave = dirty && partner.name.trim().length > 0 && colorValid && endpointValid;

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      if (partnerDirty) {
        const patch: PartnerUpdateInput = {};
        if (partner.name !== savedPartner.name) patch.name = partner.name.trim();
        if (partner.subtitle !== savedPartner.subtitle) patch.subtitle = partner.subtitle.trim() || null;
        if (partner.ctaLabel !== savedPartner.ctaLabel) patch.ctaLabel = partner.ctaLabel.trim() || 'More info';
        if (partner.color !== savedPartner.color) patch.color = partner.color;
        if (partner.logo !== savedPartner.logo) patch.logo = partner.logo;
        if (partner.leadEndpoint !== savedPartner.leadEndpoint)
          patch.leadEndpoint = partner.leadEndpoint.trim() || null;
        if (partner.active !== savedPartner.active) patch.active = partner.active;
        if (!same(partner.excluded, savedPartner.excluded))
          patch.excludedEnterprises = partner.excluded.map((e) => e._id);
        const next = toPartnerState(await client.admin.partners.update(partnerId, patch));
        setPartner(next);
        setSavedPartner(next);
      }
      if (formDirty) {
        const patch: PartnerFormUpdateInput = {};
        if (!same(form.questions, savedForm.questions)) patch.questions = form.questions;
        if (!same(form.layout, savedForm.layout)) patch.layout = form.layout;
        if (!same(form.language, savedForm.language)) patch.language = form.language;
        if (!same(form.contactFields, savedForm.contactFields))
          patch.contactFields = form.contactFields;
        const next = toFormState(await client.admin.partners.updateForm(partnerId, patch));
        setForm(next);
        setSavedForm(next);
      }
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setPartner(savedPartner);
    setForm(savedForm);
    setSaveError(null);
  };

  return (
    <div className="space-y-4">
      {(dirty || saveError) && (
        <Card className="sticky top-[calc(var(--header-h,4rem)+0.5rem)] z-20 flex flex-wrap items-center justify-between gap-3 border-primary/30 px-4 py-3 shadow-pop">
          <div className="font-mono text-[11px]">
            {saveError ? (
              <span className="text-destructive">{saveError}</span>
            ) : (
              <span className="text-muted-foreground">Unsaved changes</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={discard} disabled={saving || !dirty}>
              Discard
            </Button>
            <Button size="sm" onClick={() => void save()} disabled={saving || !canSave}>
              {saving && <Loader2 className="animate-spin" />}
              Save changes
            </Button>
          </div>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)]">
        <div className="space-y-4">
          <SectionCard title="Button" subtitle="How the partner button looks on proptrees" icon={Palette} defaultOpen>
            <div className="grid grid-cols-12 gap-4 p-5">
              <div className="col-span-12 sm:col-span-7">
                <Label htmlFor="p-name">Name</Label>
                <Input id="p-name" className="mt-1" value={partner.name} maxLength={60} onChange={(e) => setP('name', e.target.value)} />
              </div>
              <div className="col-span-12 sm:col-span-5">
                <Label htmlFor="p-cta">Button label</Label>
                <Input id="p-cta" className="mt-1" value={partner.ctaLabel} maxLength={24} placeholder="More info" onChange={(e) => setP('ctaLabel', e.target.value)} />
              </div>
              <div className="col-span-12">
                <Label htmlFor="p-sub">Subtitle (optional)</Label>
                <Input id="p-sub" className="mt-1" value={partner.subtitle} maxLength={120} placeholder="Find the best home loan in the UAE" onChange={(e) => setP('subtitle', e.target.value)} />
              </div>
              <div className="col-span-12 sm:col-span-5">
                <Label htmlFor="p-color">Brand colour</Label>
                <ColorField id="p-color" value={partner.color} onChange={(v) => setP('color', v)} />
              </div>
              <div className="col-span-12 sm:col-span-7">
                <Label>Logo</Label>
                <LogoField value={partner.logo} onChange={(v) => setP('logo', v)} />
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Lead delivery" subtitle="Where leads from the partner’s form are sent" icon={Send} defaultOpen>
            <div className="space-y-4 p-5">
              <div>
                <Label htmlFor="p-endpoint">Lead endpoint</Label>
                <Input
                  id="p-endpoint"
                  className={endpointValid ? 'mt-1 font-mono' : 'mt-1 border-destructive font-mono'}
                  value={partner.leadEndpoint}
                  placeholder="https://partner.example.com/webhooks/proptree"
                  onChange={(e) => setP('leadEndpoint', e.target.value)}
                />
                <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                  Each lead is POSTed here as JSON the moment the visitor submits the form
                  (event <code>lead.created</code>, header <code>x-proptr-partner: {initialPartner.key}</code>).
                  Without an endpoint, leads are kept but not sent.
                </p>
              </div>
              <TestLead
                partnerId={partnerId}
                disabled={!savedPartner.leadEndpoint || partner.leadEndpoint !== savedPartner.leadEndpoint}
              />
              <details className="rounded-md border border-border bg-muted/30 dark:border-white/10">
                <summary className="cursor-pointer px-3 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  Payload example
                </summary>
                <pre className="overflow-x-auto border-t border-[hsl(var(--hairline))] px-3 py-2 font-mono text-[10px] leading-relaxed">{PAYLOAD_EXAMPLE(initialPartner.key, partner.name)}</pre>
              </details>
            </div>
          </SectionCard>

          <SectionCard title="Visibility" subtitle="Whether agencies can switch this partner on" icon={EyeOff} defaultOpen>
            <div className="space-y-5 p-5">
              <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-muted/30 px-4 py-3 dark:border-white/10">
                <div>
                  <div className="text-[13px] font-medium">{partner.active ? 'Live' : 'Paused'}</div>
                  <div className="text-[12px] text-muted-foreground">
                    {partner.active
                      ? 'Agencies and agents can switch it on.'
                      : 'Hidden everywhere. Agencies keep their toggle choice for when it’s back.'}
                  </div>
                </div>
                <Switch checked={partner.active} onCheckedChange={(v) => setP('active', v)} aria-label="Partner live" />
              </div>
              <AgencyExclusions value={partner.excluded} onChange={(v) => setP('excluded', v)} />
            </div>
          </SectionCard>

          <SectionCard title="Form" subtitle="Opens when a visitor taps the partner button" icon={FileText} defaultOpen>
            <div className="grid grid-cols-12 gap-4 border-b border-[hsl(var(--hairline))] p-5">
              <div className="col-span-6">
                <Label>Layout</Label>
                <EnumSelect options={LAYOUT_OPTIONS} value={form.layout} onChange={(v) => setF('layout', v)} />
              </div>
              <div className="col-span-6">
                <Label>Language</Label>
                <EnumSelect options={LANGUAGE_OPTIONS} value={form.language} onChange={(v) => setF('language', v)} />
              </div>
              <div className="col-span-12">
                <Label>Contact fields</Label>
                <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {CONTACT_FIELDS.map(({ key, label }) => (
                    <div key={key} className="flex h-11 items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3.5 dark:border-white/10">
                      <span className="text-[13px]">{label}</span>
                      <Switch
                        checked={form.contactFields[key]}
                        onCheckedChange={(v) => setF('contactFields', { ...form.contactFields, [key]: v })}
                        aria-label={`Ask for ${label.toLowerCase()}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                  Shown after the questions as plain inputs — no verification. Turn both
                  off to send the answers only.
                </p>
              </div>
            </div>
            <div className="p-5">
              <QuestionsEditor questions={form.questions} onChange={(q) => setF('questions', q)} />
            </div>
          </SectionCard>

          <DeletePartner partnerId={partnerId} name={savedPartner.name} />
        </div>

        <div className="space-y-4 lg:sticky lg:top-[calc(var(--header-h,4rem)+1rem)] lg:self-start">
          <Card className="p-0">
            <div className="border-b border-[hsl(var(--hairline))] px-5 py-3">
              <span className="mono-label text-muted-foreground">Button preview</span>
            </div>
            <div className="bg-muted/40 p-5">
              <PartnerButtonPreview
                name={partner.name}
                subtitle={partner.subtitle.trim() || null}
                color={colorValid ? partner.color : savedPartner.color}
                logo={partner.logo}
                ctaLabel={partner.ctaLabel}
              />
            </div>
          </Card>
          <FormPreview formId={initialForm._id} name={partner.name} form={form} />
        </div>
      </div>
    </div>
  );
}

function EnumSelect({
  options,
  value,
  onChange,
}: {
  options: ProptreeEnumValue[];
  value: ProptreeEnumValue | null;
  onChange: (v: ProptreeEnumValue | null) => void;
}) {
  return (
    <Select<string>
      className="mt-1"
      options={options.map<SelectOption>((o) => ({ value: String(o.value), label: o.name ?? '' }))}
      value={value ? String(value.value) : String(options[0]?.value ?? '')}
      onChange={(v) => onChange(options.find((o) => String(o.value) === v) ?? null)}
    />
  );
}

function LogoField({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const handle = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      onChange(await uploadPartnerLogo(file));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  return (
    <div className="mt-1">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-white dark:border-white/10">
          {value ? <img src={value} alt="" className="h-7 w-7 object-contain" /> : <ImageIcon className="h-4 w-4 text-muted-foreground" />}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/svg+xml,image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handle(f);
          }}
        />
        <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}>
          {busy ? <Loader2 className="animate-spin" /> : <ImageIcon />}
          {busy ? 'Uploading…' : value ? 'Replace' : 'Upload'}
        </Button>
        {value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)} disabled={busy}>
            Remove
          </Button>
        )}
      </div>
      {error && <p className="mt-1.5 text-[12px] text-destructive">{error}</p>}
    </div>
  );
}

function TestLead({ partnerId, disabled }: { partnerId: string; disabled: boolean }) {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState<{ ok: boolean; text: string } | null>(null);
  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await client.admin.partners.sendTestLead(partnerId);
      setResult({
        ok: r.ok,
        text: r.ok ? `Delivered — endpoint answered ${r.status}` : `Not delivered — ${r.status}`,
      });
    } catch (err) {
      setResult({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button type="button" variant="outline" size="sm" onClick={() => void run()} disabled={busy || disabled}>
        {busy ? <Loader2 className="animate-spin" /> : <Send />}
        Send test lead
      </Button>
      {disabled && !result && (
        <span className="font-mono text-[10px] text-muted-foreground">Save the endpoint first.</span>
      )}
      {result && (
        <span className={`flex items-center gap-1.5 font-mono text-[11px] ${result.ok ? 'text-success' : 'text-destructive'}`}>
          {result.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
          {result.text}
        </span>
      )}
    </div>
  );
}

// Agencies the partner is hidden for. Search adds, chips remove.
function AgencyExclusions({
  value,
  onChange,
}: {
  value: Array<{ _id: string; name: string }>;
  onChange: (v: Array<{ _id: string; name: string }>) => void;
}) {
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState<EnterpriseListItem[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      return;
    }
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      client.admin
        .listEnterprises({ search: q, limit: 8 })
        .then((r) => alive && setResults(r.items))
        .catch(() => alive && setResults([]))
        .finally(() => alive && setLoading(false));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [query]);

  const excludedIds = new Set(value.map((e) => e._id));
  const nameOf = (e: EnterpriseListItem) => e.companyName || e.name || 'Unnamed agency';

  return (
    <div>
      <Label>Hidden for agencies</Label>
      <p className="mt-0.5 text-[12px] text-muted-foreground">
        These agencies don’t see the partner in their settings, and it never shows on
        their proptrees.
      </p>
      {value.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {value.map((e) => (
            <span key={e._id} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 py-1 pl-3 pr-1 text-[12px] dark:border-white/10">
              {e.name}
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x._id !== e._id))}
                className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label={`Show for ${e.name} again`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-2 font-mono text-[11px] text-muted-foreground">Available to every agency.</p>
      )}
      <div className="relative mt-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search agencies to hide it for…"
          className="pl-8"
        />
      </div>
      {query.trim() && (
        <div className="mt-1 overflow-hidden rounded-md border border-border dark:border-white/10">
          {loading && results.length === 0 ? (
            <div className="px-3 py-2 font-mono text-[11px] text-muted-foreground">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 font-mono text-[11px] text-muted-foreground">No agencies match.</div>
          ) : (
            results.map((e) => {
              const hidden = excludedIds.has(e._id);
              return (
                <button
                  key={e._id}
                  type="button"
                  disabled={hidden}
                  onClick={() => {
                    onChange([...value, { _id: e._id, name: nameOf(e) }]);
                    setQuery('');
                  }}
                  className="flex w-full items-center justify-between gap-3 border-b border-[hsl(var(--hairline))] px-3 py-2 text-left text-[13px] last:border-0 hover:bg-muted disabled:cursor-default disabled:opacity-50"
                >
                  <span className="truncate">{nameOf(e)}</span>
                  <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                    {hidden ? 'Hidden' : 'Hide'}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function DeletePartner({ partnerId, name }: { partnerId: string; name: string }) {
  const navigate = useNavigate();
  const [armed, setArmed] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  const run = async () => {
    if (!armed) return setArmed(true);
    setBusy(true);
    try {
      await client.admin.partners.remove(partnerId);
      navigate('/partners');
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };
  return (
    <Card className="flex flex-wrap items-center justify-between gap-3 border-destructive/30 p-5">
      <div>
        <div className="text-[13px] font-medium">Delete partner</div>
        <div className="text-[12px] text-muted-foreground">
          Removes {name} and its form from every proptree. Leads already sent stay with the partner.
        </div>
        {error && <div className="mt-1 text-[12px] text-destructive">{error}</div>}
      </div>
      <Button variant={armed ? 'destructive' : 'outline'} size="sm" onClick={() => void run()} disabled={busy}>
        {busy ? <Loader2 className="animate-spin" /> : <Trash2 />}
        {armed ? 'Click again to delete' : 'Delete'}
      </Button>
    </Card>
  );
}

// Live form preview — the landing app's form page in preview mode, fed
// the unsaved form over postMessage (same bridge as the manager's form
// editor).
function FormPreview({ formId, name, form }: { formId: string; name: string; form: FormState }) {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const patch = React.useMemo(
    () => ({
      name: name || null,
      questions: form.questions,
      layout: typeof form.layout?.value === 'string' ? form.layout.value : null,
      qualificationType: null,
      partner: { name: name || 'the partner', contactFields: form.contactFields },
    }),
    [name, form],
  );
  const patchRef = React.useRef(patch);
  patchRef.current = patch;

  React.useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'proptr:form-preview', patch }, '*');
  }, [patch]);
  React.useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.data && typeof e.data === 'object' && e.data.type === 'proptr:form-preview-ready') {
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'proptr:form-preview', patch: patchRef.current },
          '*',
        );
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <Card className="flex h-[620px] flex-col overflow-hidden p-0">
      <div className="border-b border-[hsl(var(--hairline))] px-5 py-3">
        <span className="mono-label text-muted-foreground">Form preview</span>
      </div>
      <iframe
        ref={iframeRef}
        src={`${PUBLIC_URL}/form/${formId}?preview=1`}
        title="Partner form preview"
        className="min-h-0 w-full flex-1 border-0 bg-muted/40"
      />
    </Card>
  );
}

const PAYLOAD_EXAMPLE = (key: string, name: string) =>
  JSON.stringify(
    {
      event: 'lead.created',
      partner: { key, name: name || 'Partner' },
      lead: {
        id: '6713…',
        name: null,
        email: 'sara@example.com',
        phone: '+971501234567',
        answers: [{ question: 'Are you a first-time buyer?', answer: 'Yes' }],
      },
      source: {
        agency: 'Hamiltons',
        proptree: { slug: 'hamiltons', type: 'agency', name: 'Hamiltons' },
      },
      createdAt: '2026-10-17T12:00:00.000Z',
    },
    null,
    2,
  );
